import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePermisoOperaciones } from './permisos';
import './office_panel.css';
import './consultas_ia.css';
import './agentes.css';

// Pantalla "Agentes" (superadmin con permiso Operaciones): tareas que se ejecutan solas en el dia y la hora elegidos, revisan
// algo y mandan mails. Las reglas son fijas; aca se configuran horarios, umbrales, destinatarios y textos.
const CAMPOS_AGENTE = `
  clave nombre descripcion destinatariosFijos tituloMensaje variables activo dias hora requiereAprobacion asunto cuerpo
  recordatorio { titulo variables } asuntoRecordatorio cuerpoRecordatorio
  parametros { clave valor tipo rotulo unidad min max opciones { valor texto } }
  destinatarios { admins interesadosAdmins interesadosInforme interesadosApi inactivos }
  proximaEjecucion ultimaEjecucion ultimoResultado ultimoOk ejecutandose
`;
const GET_AGENTES = gql`
  query GetAgentesOffice {
    getAgentesOffice {
      casilla zonaHoraria enviosPendientes enviosFallidos agendaLatido agendaActiva
      casillas { clave nombre direccion configurada }
      agentes { ${CAMPOS_AGENTE} }
    }
  }
`;
const GET_INTERESADOS = gql`
  query GetInteresadosOffice($lista: String!) {
    getInteresadosOffice(lista: $lista) { id lista email nombre nota creado creadoPor }
  }
`;
const GET_ENVIOS = gql`
  query GetEnviosAgentes($estado: String, $pagina: Int, $porPagina: Int) {
    getEnviosAgentes(estado: $estado, pagina: $pagina, porPagina: $porPagina) {
      total pagina paginas porPagina
      items { id agente agenteNombre para nombre asunto cuerpo adjuntoNombre adjuntoDisponible estado origen creado enviadoEn desde error aprobadoPor }
    }
  }
`;
const GET_MONITOREO = gql`
  query GetAdminsMonitoreoOffice {
    getAdminsMonitoreoOffice { registroDesde admins { id email nombre rol esAdmin monitoreado ultimoIngreso } }
  }
`;
const GET_ADJUNTO = gql`query GetAdjuntoEnvioAgente($id: ID!) { getAdjuntoEnvioAgente(id: $id) { nombre tipo base64 } }`;
const GET_EJECUCIONES = gql`
  query GetEjecucionesAgentes($limite: Int) {
    getEjecucionesAgentes(limite: $limite) { id agente agenteNombre origen actor inicio ok resumen enviados pendientes fallidos omitidos }
  }
`;
const ACTUALIZAR = gql`mutation A($clave: ID!, $datos: AgenteInput!) { actualizarAgenteOffice(clave: $clave, datos: $datos) { ${CAMPOS_AGENTE} } }`;
const ELEGIR_CASILLA = gql`mutation C($casilla: String!) { elegirCasillaAgentes(casilla: $casilla) }`;
const PROBAR = gql`mutation P($clave: ID!) { probarAgenteOffice(clave: $clave) { resumen requiereAprobacion total omitidos mensajes { tipo para nombre asunto cuerpo adjuntoNombre adjuntoBase64 } } }`;
const EJECUTAR = gql`mutation E($clave: ID!) { ejecutarAgenteOffice(clave: $clave) { ok resumen } }`;
const AGREGAR = gql`mutation I($lista: String!, $email: String!, $nombre: String, $nota: String) { agregarInteresadoOffice(lista: $lista, email: $email, nombre: $nombre, nota: $nota) { id } }`;
const QUITAR = gql`mutation Q($id: ID!) { quitarInteresadoOffice(id: $id) }`;
const APROBAR = gql`mutation AP($id: ID!) { aprobarEnvioAgente(id: $id) { id estado error } }`;
const DESCARTAR = gql`mutation D($id: ID!) { descartarEnvioAgente(id: $id) { id estado } }`;

const TZ = 'America/Argentina/Buenos_Aires';
const DIAS = [[1, 'Lun'], [2, 'Mar'], [3, 'Mié'], [4, 'Jue'], [5, 'Vie'], [6, 'Sáb'], [7, 'Dom']];
const PESTANAS = [
  ['resumen', 'Resumen'],
  ['admins_inactivos', 'Admins inactivos'],
  ['informe_trafico', 'Informe NAWI Chat'],
  ['uso_api', 'Uso de la API'],
  ['aviso_api', 'Aviso por la API'],
  ['admins_monitoreo', 'Admins a monitorear'],
  ['lista_admins', 'Lista de interesados admins'],
  ['lista_informe', 'Lista de interesados informe'],
  ['lista_api', 'Lista de interesados API'],
  ['envios', 'Envíos'],
];
const LISTAS = {
  admins: {
    titulo: 'Lista de interesados admins',
    texto: 'Reciben el aviso del agente "Admins inactivos" cuando alguno de los admins que monitoreás no entra a Office hace un tiempo.',
  },
  informe: {
    titulo: 'Lista de interesados informe',
    texto: 'Reciben el informe en PDF de NAWI Chat cuando el agente "Informe NAWI Chat" tiene marcada esta lista.',
  },
  api: {
    titulo: 'Lista de interesados API',
    texto: 'Reciben el aviso del agente "Aviso por la API" cuando nadie usa la API de preproducción. También el resumen de uso, si lo marcás en "Uso de la API".',
  },
};
const ESTADOS = {
  pendiente: ['tag--yellow', 'Esperando aprobación'],
  enviando: ['tag--blue', 'Enviando'],
  enviado: ['tag--green', 'Enviado'],
  fallido: ['tag--red', 'Falló'],
  descartado: ['tag--gray', 'Descartado'],
};
const ORIGENES = { agenda: 'Agenda', manual: 'A mano', prueba: 'Prueba sin enviar' };
// Grupos de destinatarios que se pueden marcar en cualquier agente: [clave, texto, pestaña de la lista].
const GRUPOS = [
  ['interesadosAdmins', 'Lista de interesados admins', 'lista_admins'],
  ['interesadosInforme', 'Lista de interesados informe', 'lista_informe'],
  ['interesadosApi', 'Lista de interesados API', 'lista_api'],
  ['admins', 'Todos los admins de Office', null],
];

const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(/-/g, '/') : '');
const fechaLarga = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '');
function haceCuanto(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'hace instantes';
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}
const diasTexto = (dias) => {
  if (!dias || !dias.length) return 'ningún día';
  if (dias.length === 7) return 'todos los días';
  if (dias.join() === '1,2,3,4,5') return 'de lunes a viernes';
  return dias.map((d) => DIAS.find(([n]) => n === d)[1]).join(', ');
};
const errorDe = (e) => (e && (e.graphQLErrors?.[0]?.message || e.message)) || 'No se pudo completar la operación.';

function abrirPdf(base64, nombre) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const ventana = window.open(url, '_blank', 'noopener');
  if (!ventana) {
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre || 'informe.pdf';
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function Aviso({ tipo = 'info', children }) {
  return <div className={`ag-aviso ag-aviso--${tipo}`}>{children}</div>;
}

// ── Resumen ────────────────────────────────────────────────────────────────────────
function Resumen({ datos, ir, onCasilla, guardandoCasilla }) {
  return (
    <>
      {datos.agendaActiva ? (
        <Aviso tipo="ok">La agenda está funcionando: revisó los agentes {haceCuanto(datos.agendaLatido)}.</Aviso>
      ) : (
        <Aviso tipo="error">
          La agenda no está funcionando{datos.agendaLatido ? ` desde ${fechaLarga(datos.agendaLatido)}` : ''}: los agentes no se van a ejecutar solos.
          Igual podés probarlos y ejecutarlos a mano.
        </Aviso>
      )}
      {(datos.enviosPendientes > 0 || datos.enviosFallidos > 0) && (
        <Aviso tipo="atencion">
          {datos.enviosPendientes > 0 && <>Hay <b>{datos.enviosPendientes}</b> {datos.enviosPendientes === 1 ? 'mail esperando' : 'mails esperando'} tu aprobación. </>}
          {datos.enviosFallidos > 0 && <><b>{datos.enviosFallidos}</b> {datos.enviosFallidos === 1 ? 'mail falló' : 'mails fallaron'}. </>}
          <button className="btn-subtle ag-enlace" onClick={() => ir('envios')}>Ver envíos</button>
        </Aviso>
      )}

      <div className="ag-grilla">
        {datos.agentes.map((a) => (
          <article key={a.clave} className="panel-card ag-tarjeta">
            <header className="ag-tarjeta-cabeza">
              <h3>{a.nombre}</h3>
              <span className={`tag ${a.activo ? 'tag--green' : 'tag--gray'}`}>{a.activo ? 'Activo' : 'Pausado'}</span>
            </header>
            <p className="ag-tarjeta-texto">{a.descripcion}</p>
            <dl className="ag-datos">
              <dt>Cuándo</dt><dd>{diasTexto(a.dias)}, a las {a.hora}</dd>
              <dt>Próxima vez</dt><dd>{a.activo && a.proximaEjecucion ? fechaHora(a.proximaEjecucion) : 'Pausado: no se ejecuta solo'}</dd>
              <dt>Última vez</dt>
              <dd>
                {a.ejecutandose ? 'Ejecutándose ahora…' : a.ultimaEjecucion ? (
                  <>{fechaHora(a.ultimaEjecucion)} <span className={`tag ${a.ultimoOk ? 'tag--green' : 'tag--red'}`}>{a.ultimoOk ? 'Bien' : 'Con error'}</span></>
                ) : 'Todavía no se ejecutó'}
              </dd>
            </dl>
            <button className="btn-outline" onClick={() => ir(a.clave)}>Configurar</button>
          </article>
        ))}
      </div>

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">Casilla desde la que salen los mails</h3>
        <p className="ag-ayuda">Todos los agentes usan la misma casilla. La contraseña está en el servidor y no se ve acá.</p>
        <div className="ag-opciones">
          {datos.casillas.map((c) => (
            <label key={c.clave} className={`ag-opcion ${datos.casilla === c.clave ? 'ag-opcion--elegida' : ''}`}>
              <input type="radio" name="casilla" checked={datos.casilla === c.clave} disabled={guardandoCasilla || !c.configurada}
                onChange={() => onCasilla(c.clave)} />
              <span>
                <b>{c.nombre}</b>
                <small>{c.direccion || 'Sin dirección configurada'}{!c.configurada ? ' · no está configurada en el servidor' : ''}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </>
  );
}

// ── Configuracion de un agente ─────────────────────────────────────────────────────
function estadoInicial(a) {
  return {
    activo: a.activo, dias: a.dias, hora: a.hora, requiereAprobacion: a.requiereAprobacion,
    asunto: a.asunto, cuerpo: a.cuerpo, asuntoRecordatorio: a.asuntoRecordatorio || '', cuerpoRecordatorio: a.cuerpoRecordatorio || '',
    parametros: Object.fromEntries(a.parametros.map((p) => [p.clave, p.valor])),
    destinatarios: Object.fromEntries(['admins', 'interesadosAdmins', 'interesadosInforme', 'interesadosApi', 'inactivos'].map((k) => [k, !!a.destinatarios[k]])),
  };
}

function EditorMensaje({ titulo, nota, asunto, cuerpo, variables, onAsunto, onCuerpo }) {
  const cuerpoRef = useRef(null);
  const insertar = (v) => {
    const el = cuerpoRef.current;
    const marca = `{${v}}`;
    if (!el) { onCuerpo(cuerpo + marca); return; }
    const ini = el.selectionStart ?? cuerpo.length;
    const fin = el.selectionEnd ?? cuerpo.length;
    onCuerpo(cuerpo.slice(0, ini) + marca + cuerpo.slice(fin));
    setTimeout(() => { el.focus(); el.setSelectionRange(ini + marca.length, ini + marca.length); }, 0);
  };
  return (
    <section className="panel-card ag-seccion">
      <h3 className="ag-titulo">{titulo}</h3>
      {nota && <p className="ag-ayuda ag-ayuda--arriba">{nota}</p>}
      <label className="ag-campo">
        <span>Asunto</span>
        <input type="text" value={asunto} maxLength={200} onChange={(e) => onAsunto(e.target.value)} />
      </label>
      <label className="ag-campo">
        <span>Texto</span>
        <textarea ref={cuerpoRef} rows={13} value={cuerpo} maxLength={5000} onChange={(e) => onCuerpo(e.target.value)} />
      </label>
      <div className="ag-variables">
        <span>Insertar:</span>
        {variables.map((v) => <button key={v} type="button" className="ag-variable" onClick={() => insertar(v)}>{`{${v}}`}</button>)}
      </div>
      <p className="ag-ayuda">Cada variable se reemplaza por su valor para cada destinatario; por ejemplo, {'{nombre}'} por su nombre.</p>
    </section>
  );
}

// Admins que vigila "Admins inactivos": se eligen en su pestaña; aca se muestran para tenerlos a la vista.
function Vigilados({ ir }) {
  const { data, loading } = useQuery(GET_MONITOREO, { fetchPolicy: 'network-only' });
  const xs = (data?.getAdminsMonitoreoOffice?.admins || []).filter((a) => a.monitoreado);
  return (
    <div className="ag-vigilados">
      <div className="ag-vigilados-cabeza">
        <span>Admins que vigila</span>
        <button type="button" className="btn-subtle ag-enlace" onClick={() => ir('admins_monitoreo')}>{xs.length ? 'cambiar' : 'elegir admins'}</button>
      </div>
      {loading && !data ? <div className="spinner" /> : xs.length === 0 ? (
        <p className="ag-aviso ag-aviso--atencion">Todavía no elegiste a ningún admin: el agente no tiene a quién vigilar.</p>
      ) : (
        <div className="ag-chips">
          {xs.map((a) => <span key={a.email} className={`ag-chip ${a.esAdmin ? '' : 'ag-chip--apagado'}`} title={a.esAdmin ? a.email : 'Ya no es admin con acceso a Office'}>{a.nombre}</span>)}
        </div>
      )}
    </div>
  );
}

function EjemploMail({ m, titulo }) {
  return (
    <div className="ag-mail">
      <div className="ag-mail-cabeza"><span>{titulo || `Así le llega a ${m.nombre || m.para}`}</span>{m.adjuntoNombre && <span className="tag tag--purple">PDF adjunto</span>}</div>
      <div className="ag-mail-asunto">{m.asunto}</div>
      <div className="ag-mail-cuerpo">{m.cuerpo}</div>
      {m.adjuntoBase64 && (
        <button className="btn-outline" onClick={() => abrirPdf(m.adjuntoBase64, m.adjuntoNombre)}>Ver el PDF</button>
      )}
    </div>
  );
}

function Prueba({ r, onCerrar }) {
  const avisos = r.mensajes.filter((m) => m.tipo !== 'recordatorio');
  const recordatorios = r.mensajes.filter((m) => m.tipo === 'recordatorio');
  const dosTipos = avisos.length > 0 && recordatorios.length > 0;
  return (
    <section className="panel-card ag-seccion ag-prueba">
      <header className="ag-prueba-cabeza">
        <h3 className="ag-titulo">Prueba sin enviar</h3>
        <button className="btn-subtle" onClick={onCerrar}>Cerrar</button>
      </header>
      <p className="ag-pre">{r.resumen}</p>
      <p className="ag-ayuda">
        {r.total ? <>Si se ejecutara ahora, {r.requiereAprobacion ? 'dejaría para tu aprobación' : 'mandaría'} <b>{r.total}</b> {r.total === 1 ? 'mail' : 'mails'}.</> : 'Si se ejecutara ahora, no mandaría ningún mail.'}
        {r.omitidos.length > 0 && <> Se saltearía a {r.omitidos.length} {r.omitidos.length === 1 ? 'persona que ya recibió' : 'personas que ya recibieron'} este aviso hace poco.</>}
      </p>
      {[[avisos, dosTipos ? 'Aviso a los interesados' : null], [recordatorios, dosTipos || recordatorios.length ? 'Recordatorio a los admins inactivos' : null]].map(([xs, titulo]) => xs.length > 0 && (
        <div key={titulo || 'mails'} className="ag-prueba-grupo">
          {titulo && <h4 className="ag-subtitulo-prueba">{titulo}</h4>}
          <div className="ag-para">Para: {xs.map((m) => m.para).join(', ')}</div>
          <EjemploMail m={xs[0]} />
        </div>
      ))}
    </section>
  );
}

function ConfigAgente({ agente, ir, onFlash, refetch }) {
  const [f, setF] = useState(() => estadoInicial(agente));
  const [prueba, setPrueba] = useState(null);
  const [confirmar, setConfirmar] = useState(false);
  const [actualizar, { loading: guardando }] = useMutation(ACTUALIZAR);
  const [probar, { loading: probando }] = useMutation(PROBAR);
  const [ejecutar, { loading: ejecutando }] = useMutation(EJECUTAR);
  const original = useMemo(() => JSON.stringify(estadoInicial(agente)), [agente]);
  const cambiado = JSON.stringify(f) !== original;

  useEffect(() => { if (!cambiado) setF(estadoInicial(agente)); }, [original]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setParam = (k, v) => setF((x) => ({ ...x, parametros: { ...x.parametros, [k]: v } }));
  const setDest = (k, v) => setF((x) => ({ ...x, destinatarios: { ...x.destinatarios, [k]: v } }));
  const toggleDia = (d) => set('dias', f.dias.includes(d) ? f.dias.filter((x) => x !== d) : [...f.dias, d].sort());

  const guardar = async () => {
    try {
      await actualizar({ variables: { clave: agente.clave, datos: {
        activo: f.activo, dias: f.dias, hora: f.hora, requiereAprobacion: f.requiereAprobacion, asunto: f.asunto, cuerpo: f.cuerpo,
        ...(agente.recordatorio ? { asuntoRecordatorio: f.asuntoRecordatorio, cuerpoRecordatorio: f.cuerpoRecordatorio } : {}),
        parametros: Object.entries(f.parametros).map(([clave, valor]) => ({ clave, valor: String(valor) })),
        ...(agente.destinatariosFijos ? {} : { destinatarios: f.destinatarios }),
      } } });
      onFlash('Cambios guardados.');
      refetch();
    } catch (e) { onFlash(errorDe(e), 'error'); }
  };
  const correrPrueba = async () => {
    setPrueba(null);
    try {
      const { data } = await probar({ variables: { clave: agente.clave } });
      setPrueba(data.probarAgenteOffice);
    } catch (e) { onFlash(errorDe(e), 'error'); }
  };
  const correrAhora = async () => {
    setConfirmar(false);
    try {
      const { data } = await ejecutar({ variables: { clave: agente.clave } });
      onFlash(data.ejecutarAgenteOffice.resumen);
      refetch();
    } catch (e) { onFlash(errorDe(e), 'error'); }
  };

  return (
    <>
      <section className="panel-card ag-seccion ag-cabecera-agente">
        <div>
          <h2 className="ag-nombre">{agente.nombre}</h2>
          <p className="ag-tarjeta-texto">{agente.descripcion}</p>
        </div>
        <label className="ag-interruptor">
          <input type="checkbox" checked={f.activo} onChange={(e) => set('activo', e.target.checked)} />
          <span className="ag-interruptor-pista" aria-hidden="true"><span /></span>
          <span className="ag-interruptor-texto">{f.activo ? 'Activo: se ejecuta solo' : 'Pausado'}</span>
        </label>
      </section>

      <div className="ag-columnas">
        <div>
          <section className="panel-card ag-seccion">
            <h3 className="ag-titulo">Cuándo se ejecuta</h3>
            <div className="ag-dias" role="group" aria-label="Días de la semana">
              {DIAS.map(([d, t]) => (
                <button key={d} type="button" aria-pressed={f.dias.includes(d)}
                  className={`ag-dia ${f.dias.includes(d) ? 'ag-dia--elegido' : ''}`} onClick={() => toggleDia(d)}>{t}</button>
              ))}
            </div>
            <label className="ag-campo ag-campo--corto">
              <span>Hora</span>
              <input type="time" value={f.hora} onChange={(e) => set('hora', e.target.value)} />
            </label>
            <p className="ag-ayuda">Hora de Argentina. {agente.activo && agente.proximaEjecucion ? `Próxima ejecución: ${fechaHora(agente.proximaEjecucion)}.` : ''}</p>
          </section>

          <section className="panel-card ag-seccion">
            <h3 className="ag-titulo">Qué revisa</h3>
            {agente.clave === 'admins_inactivos' && <Vigilados ir={ir} />}
            {agente.parametros.map((p, i) => {
              const anterior = agente.parametros[i - 1];
              const siguiente = agente.parametros[i + 1];
              // Una opcion que sigue a un numero va en la misma linea, como su unidad: "al menos [1] [preguntas]".
              if (p.tipo === 'opcion' && anterior && anterior.tipo === 'entero') return null;
              const selector = (q) => (
                <select value={f.parametros[q.clave]} aria-label={q.rotulo} onChange={(e) => setParam(q.clave, e.target.value)}>
                  {q.opciones.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
                </select>
              );
              if (p.tipo === 'si_no') {
                return (
                  <label key={p.clave} className="ag-campo ag-campo--check">
                    <input type="checkbox" checked={String(f.parametros[p.clave]) === 'true'} onChange={(e) => setParam(p.clave, String(e.target.checked))} />
                    <span>{p.rotulo}</span>
                  </label>
                );
              }
              if (p.tipo === 'opcion') {
                return <label key={p.clave} className="ag-campo"><span>{p.rotulo}</span>{selector(p)}</label>;
              }
              return (
                <label key={p.clave} className="ag-campo">
                  <span>{p.rotulo}</span>
                  <span className="ag-entrada-unidad">
                    <input type={p.tipo === 'fecha' ? 'date' : 'number'} min={p.min ?? undefined} max={p.max ?? undefined}
                      value={f.parametros[p.clave]} onChange={(e) => setParam(p.clave, e.target.value)} />
                    {siguiente && siguiente.tipo === 'opcion' ? selector(siguiente) : p.unidad ? <small>{p.unidad}</small> : null}
                  </span>
                </label>
              );
            })}
          </section>

          <section className="panel-card ag-seccion">
            <h3 className="ag-titulo">A quién le escribe</h3>
            {agente.destinatariosFijos ? <p className="ag-ayuda">{agente.destinatariosFijos}</p> : (
              <>
                {GRUPOS.map(([k, t, pestana]) => (
                  <label key={k} className="ag-campo ag-campo--check">
                    <input type="checkbox" checked={!!f.destinatarios[k]} onChange={(e) => setDest(k, e.target.checked)} />
                    <span>{t}{pestana && <button type="button" className="btn-subtle ag-enlace" onClick={() => ir(pestana)}>editar lista</button>}</span>
                  </label>
                ))}
                {agente.recordatorio && (
                  <label className="ag-campo ag-campo--check ag-campo--separado">
                    <input type="checkbox" checked={!!f.destinatarios.inactivos} onChange={(e) => setDest('inactivos', e.target.checked)} />
                    <span>Además, cada admin inactivo recibe un recordatorio para que vuelva a entrar</span>
                  </label>
                )}
              </>
            )}
            <h3 className="ag-titulo ag-titulo--separado">Cómo salen los mails</h3>
            <label className="ag-campo ag-campo--check">
              <input type="radio" name={`aprobacion-${agente.clave}`} checked={!f.requiereAprobacion} onChange={() => set('requiereAprobacion', false)} />
              <span>Se envían solos</span>
            </label>
            <label className="ag-campo ag-campo--check">
              <input type="radio" name={`aprobacion-${agente.clave}`} checked={f.requiereAprobacion} onChange={() => set('requiereAprobacion', true)} />
              <span>Quedan en Envíos hasta que yo los apruebe</span>
            </label>
          </section>
        </div>

        <div>
          <EditorMensaje titulo={agente.tituloMensaje || 'Mensaje'} asunto={f.asunto} cuerpo={f.cuerpo} variables={agente.variables}
            nota={agente.recordatorio ? 'Le llega a cada persona de las listas marcadas, con todos los admins inactivos juntos. {inactivos} es esa lista.' : null}
            onAsunto={(v) => set('asunto', v)} onCuerpo={(v) => set('cuerpo', v)} />
          {agente.recordatorio && f.destinatarios.inactivos && (
            <EditorMensaje titulo={agente.recordatorio.titulo} asunto={f.asuntoRecordatorio} cuerpo={f.cuerpoRecordatorio} variables={agente.recordatorio.variables}
              nota="Le llega a cada admin inactivo, por separado."
              onAsunto={(v) => set('asuntoRecordatorio', v)} onCuerpo={(v) => set('cuerpoRecordatorio', v)} />
          )}
        </div>
      </div>

      <div className="ag-acciones">
        <button className="btn-accent" disabled={!cambiado || guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar cambios'}</button>
        {cambiado && <button className="btn-subtle" onClick={() => setF(estadoInicial(agente))}>Descartar cambios</button>}
        <span className="ag-espacio" />
        <button className="btn-outline" disabled={cambiado || probando} onClick={correrPrueba} title={cambiado ? 'Guardá los cambios para probarlos' : ''}>
          {probando ? 'Probando…' : 'Probar sin enviar'}
        </button>
        {confirmar ? (
          <span className="ag-confirmar">
            {agente.requiereAprobacion ? 'Va a dejar los mails para tu aprobación.' : 'Va a mandar los mails ahora.'}
            <button className="btn-accent" onClick={correrAhora}>Sí, ejecutar</button>
            <button className="btn-subtle" onClick={() => setConfirmar(false)}>Cancelar</button>
          </span>
        ) : (
          <button className="btn-outline" disabled={cambiado || ejecutando || agente.ejecutandose} onClick={() => setConfirmar(true)}>
            {agente.ejecutandose ? 'Ejecutándose…' : 'Ejecutar ahora'}
          </button>
        )}
      </div>
      {cambiado && <p className="ag-ayuda ag-derecha">Guardá los cambios para probarlos o ejecutar el agente.</p>}

      {prueba && <Prueba r={prueba} onCerrar={() => setPrueba(null)} />}

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">Última ejecución</h3>
        {agente.ejecutandose ? <p className="ag-ayuda">Ejecutándose ahora…</p> : agente.ultimaEjecucion ? (
          <>
            <p className="ag-ayuda">{fechaLarga(agente.ultimaEjecucion)} <span className={`tag ${agente.ultimoOk ? 'tag--green' : 'tag--red'}`}>{agente.ultimoOk ? 'Bien' : 'Con error'}</span></p>
            <p className="ag-pre">{agente.ultimoResultado}</p>
          </>
        ) : <p className="ag-ayuda">Todavía no se ejecutó.</p>}
      </section>
    </>
  );
}

// ── Listas de interesados ──────────────────────────────────────────────────────────
function Interesados({ lista, onFlash }) {
  const { data, loading, refetch } = useQuery(GET_INTERESADOS, { variables: { lista }, fetchPolicy: 'network-only' });
  const [agregar, { loading: agregando }] = useMutation(AGREGAR);
  const [quitar] = useMutation(QUITAR);
  const [nuevo, setNuevo] = useState({ email: '', nombre: '', nota: '' });
  const [aQuitar, setAQuitar] = useState(null);
  const xs = data?.getInteresadosOffice || [];
  const info = LISTAS[lista];

  const enviar = async (e) => {
    e.preventDefault();
    try {
      await agregar({ variables: { lista, email: nuevo.email.trim(), nombre: nuevo.nombre.trim() || null, nota: nuevo.nota.trim() || null } });
      setNuevo({ email: '', nombre: '', nota: '' });
      onFlash('Agregado a la lista.');
      refetch();
    } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  const confirmarQuitar = async (id) => {
    try {
      await quitar({ variables: { id } });
      setAQuitar(null);
      onFlash('Quitado de la lista.');
      refetch();
    } catch (err) { onFlash(errorDe(err), 'error'); }
  };

  return (
    <>
      <section className="panel-card ag-seccion">
        <h2 className="ag-nombre">{info.titulo}</h2>
        <p className="ag-tarjeta-texto">{info.texto}</p>
        <form className="ag-form-fila" onSubmit={enviar}>
          <label className="ag-campo"><span>Email</span><input type="email" required value={nuevo.email} placeholder="nombre@empresa.com" onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} /></label>
          <label className="ag-campo"><span>Nombre</span><input type="text" value={nuevo.nombre} maxLength={120} placeholder="Opcional" onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} /></label>
          <label className="ag-campo"><span>Nota</span><input type="text" value={nuevo.nota} maxLength={200} placeholder="Opcional, por ejemplo la empresa" onChange={(e) => setNuevo({ ...nuevo, nota: e.target.value })} /></label>
          <button className="btn-accent" type="submit" disabled={agregando || !nuevo.email.trim()}>{agregando ? 'Agregando…' : 'Agregar'}</button>
        </form>
        <p className="ag-ayuda">Si no ponés nombre, el mail lo saluda con la primera parte del email.</p>
      </section>

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">{xs.length === 1 ? '1 persona en la lista' : `${xs.length} personas en la lista`}</h3>
        {loading && !data ? <div className="spinner" /> : xs.length === 0 ? (
          <div className="empty-state">Todavía no agregaste a nadie.</div>
        ) : (
          <div className="cia-tabla-scroll">
            <table className="cia-tabla ag-tabla">
              <thead><tr><th>Nombre</th><th>Email</th><th>Nota</th><th>Agregado</th><th /></tr></thead>
              <tbody>
                {xs.map((x) => (
                  <tr key={x.id}>
                    <td>{x.nombre || '—'}</td>
                    <td>{x.email}</td>
                    <td>{x.nota || '—'}</td>
                    <td className="ag-gris">{fechaLarga(x.creado)}{x.creadoPor ? ` · ${x.creadoPor}` : ''}</td>
                    <td className="ag-derecha">
                      {aQuitar === x.id ? (
                        <span className="ag-confirmar">
                          <button className="btn-danger" onClick={() => confirmarQuitar(x.id)}>Quitar</button>
                          <button className="btn-subtle" onClick={() => setAQuitar(null)}>Cancelar</button>
                        </span>
                      ) : <button className="btn-subtle" onClick={() => setAQuitar(x.id)}>Quitar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

// ── Admins a monitorear ────────────────────────────────────────────────────────────
function AdminsMonitoreo({ onFlash, diasInactividad }) {
  const { data, loading, refetch } = useQuery(GET_MONITOREO, { fetchPolicy: 'network-only' });
  const [agregar] = useMutation(AGREGAR);
  const [quitar] = useMutation(QUITAR);
  const [ocupado, setOcupado] = useState(null);
  const [filtro, setFiltro] = useState('');
  const m = data?.getAdminsMonitoreoOffice;
  const todos = m?.admins || [];
  const vigilados = todos.filter((a) => a.monitoreado);
  const q = filtro.trim().toLowerCase();
  const otros = todos.filter((a) => !a.monitoreado && (!q || a.nombre.toLowerCase().includes(q) || a.email.includes(q)));
  const limite = diasInactividad ? Date.now() - diasInactividad * 86400000 : null;

  const accion = async (clave, fn, ok) => {
    setOcupado(clave);
    try { await fn(); onFlash(ok); await refetch(); } catch (err) { onFlash(errorDe(err), 'error'); }
    setOcupado(null);
  };
  const monitorear = (a) => accion(a.email, () => agregar({ variables: { lista: 'monitoreados', email: a.email, nombre: null, nota: null } }), `Ahora se monitorea a ${a.nombre}.`);
  const dejar = (a) => accion(a.email, () => quitar({ variables: { id: a.id } }), `Ya no se monitorea a ${a.nombre}.`);
  const todosOtros = async () => {
    setOcupado('todos');
    let n = 0;
    try {
      for (const a of otros) { await agregar({ variables: { lista: 'monitoreados', email: a.email, nombre: null, nota: null } }); n += 1; }
      onFlash(`Se agregaron ${n} admins.`);
    } catch (err) { onFlash(errorDe(err), 'error'); }
    setOcupado(null);
    refetch();
  };
  const ingreso = (a) => {
    if (a.ultimoIngreso) {
      const viejo = limite && new Date(a.ultimoIngreso).getTime() < limite;
      return <span className={viejo ? 'ag-tarde' : ''}>{fechaLarga(a.ultimoIngreso)} <span className="ag-gris">· {haceCuanto(a.ultimoIngreso)}</span></span>;
    }
    const viejo = limite && m?.registroDesde && new Date(m.registroDesde).getTime() < limite;
    return <span className={viejo ? 'ag-tarde' : 'ag-gris'}>{m?.registroDesde ? `Sin ingresos desde el ${fechaLarga(m.registroDesde).split(',')[0]}` : 'Sin ingresos registrados'}</span>;
  };
  const rol = (a) => (!a.esAdmin ? <span className="tag tag--red">Ya no es admin</span> : a.rol === 'superadmin' ? <span className="tag tag--purple">Superadmin</span> : <span className="tag tag--gray">Admin</span>);

  return (
    <>
      <section className="panel-card ag-seccion">
        <h2 className="ag-nombre">Admins a monitorear</h2>
        <p className="ag-tarjeta-texto">
          El agente "Admins inactivos" vigila solo a estos admins. Si alguno no entra a Office {diasInactividad ? `en ${diasInactividad} días` : 'hace un tiempo'}, avisa
          a la Lista de interesados admins. Los cambios se guardan al instante.
        </p>
      </section>

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">{vigilados.length === 1 ? 'Monitoreando a 1 admin' : `Monitoreando a ${vigilados.length} admins`}</h3>
        {loading && !data ? <div className="spinner" /> : vigilados.length === 0 ? (
          <div className="empty-state">Todavía no elegiste a ningún admin. Elegilos en la lista de abajo.</div>
        ) : (
          <div className="cia-tabla-scroll">
            <table className="cia-tabla ag-tabla">
              <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Último ingreso</th><th /></tr></thead>
              <tbody>
                {vigilados.map((a) => (
                  <tr key={a.email}>
                    <td>{a.nombre}</td><td>{a.email}</td><td>{rol(a)}</td><td>{ingreso(a)}</td>
                    <td className="ag-derecha"><button className="btn-subtle" disabled={!!ocupado} onClick={() => dejar(a)}>{ocupado === a.email ? 'Quitando…' : 'Dejar de monitorear'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel-card ag-seccion">
        <div className="ag-fila-titulo">
          <h3 className="ag-titulo">Otros admins de Office</h3>
          <span className="ag-espacio" />
          <input type="search" className="ag-buscar" placeholder="Buscar por nombre o email" value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Buscar admins" />
          {otros.length > 1 && <button className="btn-outline" disabled={!!ocupado} onClick={todosOtros}>{ocupado === 'todos' ? 'Agregando…' : `Monitorear a los ${otros.length}`}</button>}
        </div>
        {loading && !data ? <div className="spinner" /> : otros.length === 0 ? (
          <div className="empty-state">{q ? 'Ningún admin coincide con la búsqueda.' : 'Ya monitoreás a todos los admins de Office.'}</div>
        ) : (
          <div className="cia-tabla-scroll">
            <table className="cia-tabla ag-tabla">
              <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Último ingreso</th><th /></tr></thead>
              <tbody>
                {otros.map((a) => (
                  <tr key={a.email}>
                    <td>{a.nombre}</td><td>{a.email}</td><td>{rol(a)}</td><td>{ingreso(a)}</td>
                    <td className="ag-derecha"><button className="btn-accent" disabled={!!ocupado} onClick={() => monitorear(a)}>{ocupado === a.email ? 'Agregando…' : 'Monitorear'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="ag-ayuda">Solo aparecen los admins de Office activos y con acceso. Las cuentas de empresa no se cuentan.</p>
      </section>
    </>
  );
}

// ── Envios ─────────────────────────────────────────────────────────────────────────
function TarjetaEnvio({ e, onFlash, refetch }) {
  const [abierto, setAbierto] = useState(false);
  const [aprobar, { loading: aprobando }] = useMutation(APROBAR);
  const [descartar, { loading: descartando }] = useMutation(DESCARTAR);
  const [pedirAdjunto, { loading: bajando }] = useLazyQuery(GET_ADJUNTO, { fetchPolicy: 'network-only' });
  const [clase, texto] = ESTADOS[e.estado] || ['tag--gray', e.estado];
  const accion = async (fn, ok) => {
    try {
      const { data } = await fn({ variables: { id: e.id } });
      const r = data.aprobarEnvioAgente || data.descartarEnvioAgente;
      if (r.estado === 'fallido') onFlash(`No salió: ${r.error}`, 'error'); else onFlash(ok);
      refetch();
    } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  const verPdf = async () => {
    const { data, error } = await pedirAdjunto({ variables: { id: e.id } });
    if (error || !data) { onFlash(errorDe(error), 'error'); return; }
    abrirPdf(data.getAdjuntoEnvioAgente.base64, data.getAdjuntoEnvioAgente.nombre);
  };
  return (
    <article className="panel-card ag-envio">
      <header className="ag-envio-cabeza">
        <span className={`tag ${clase}`}>{texto}</span>
        <b>{e.para}</b>
        <span className="ag-gris">{e.agenteNombre} · {fechaLarga(e.creado)}</span>
      </header>
      <div className="ag-mail-asunto">{e.asunto}</div>
      {e.error && <p className="ag-error">Error: {e.error}</p>}
      {abierto && <div className="ag-mail-cuerpo">{e.cuerpo}</div>}
      <div className="ag-envio-acciones">
        <button className="btn-subtle" onClick={() => setAbierto(!abierto)}>{abierto ? 'Ocultar texto' : 'Ver texto'}</button>
        {e.adjuntoDisponible && <button className="btn-subtle" disabled={bajando} onClick={verPdf}>Ver el PDF</button>}
        <span className="ag-espacio" />
        <button className="btn-subtle" disabled={descartando} onClick={() => accion(descartar, 'Envío descartado.')}>Descartar</button>
        <button className="btn-accent" disabled={aprobando} onClick={() => accion(aprobar, 'Mail enviado.')}>
          {aprobando ? 'Enviando…' : e.estado === 'fallido' ? 'Reintentar' : 'Aprobar y enviar'}
        </button>
      </div>
    </article>
  );
}

function Envios({ onFlash }) {
  const [pagina, setPagina] = useState(1);
  const pendientes = useQuery(GET_ENVIOS, { variables: { estado: 'pendiente', pagina: 1, porPagina: 50 }, fetchPolicy: 'network-only' });
  const fallidos = useQuery(GET_ENVIOS, { variables: { estado: 'fallido', pagina: 1, porPagina: 50 }, fetchPolicy: 'network-only' });
  const historial = useQuery(GET_ENVIOS, { variables: { pagina, porPagina: 20 }, fetchPolicy: 'network-only' });
  const ejecuciones = useQuery(GET_EJECUCIONES, { variables: { limite: 15 }, fetchPolicy: 'network-only' });
  const refetch = () => { pendientes.refetch(); fallidos.refetch(); historial.refetch(); ejecuciones.refetch(); };
  const p = pendientes.data?.getEnviosAgentes;
  const f = fallidos.data?.getEnviosAgentes;
  const h = historial.data?.getEnviosAgentes;
  const ej = ejecuciones.data?.getEjecucionesAgentes || [];

  return (
    <>
      <section className="ag-seccion-libre">
        <h3 className="ag-titulo">Esperando tu aprobación {p ? `(${p.total})` : ''}</h3>
        {p && p.items.length === 0 && <div className="panel-card empty-state">No hay mails esperando.</div>}
        {p && p.items.map((e) => <TarjetaEnvio key={e.id} e={e} onFlash={onFlash} refetch={refetch} />)}
      </section>
      {f && f.items.length > 0 && (
        <section className="ag-seccion-libre">
          <h3 className="ag-titulo">Fallidos ({f.total})</h3>
          {f.items.map((e) => <TarjetaEnvio key={e.id} e={e} onFlash={onFlash} refetch={refetch} />)}
        </section>
      )}

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">Historial de mails</h3>
        {!h ? <div className="spinner" /> : h.total === 0 ? <div className="empty-state">Todavía no salió ningún mail.</div> : (
          <>
            <div className="cia-tabla-scroll">
              <table className="cia-tabla ag-tabla">
                <thead><tr><th>Fecha</th><th>Agente</th><th>Para</th><th>Asunto</th><th>Estado</th></tr></thead>
                <tbody>
                  {h.items.map((e) => {
                    const [clase, texto] = ESTADOS[e.estado] || ['tag--gray', e.estado];
                    return (
                      <tr key={e.id}>
                        <td className="ag-gris">{fechaLarga(e.enviadoEn || e.creado)}</td>
                        <td>{e.agenteNombre}</td>
                        <td>{e.para}</td>
                        <td>{e.asunto}{e.adjuntoNombre ? ' · PDF' : ''}</td>
                        <td><span className={`tag ${clase}`} title={e.error || ''}>{texto}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {h.paginas > 1 && (
              <div className="ag-paginas">
                <button className="btn-outline" disabled={h.pagina <= 1} onClick={() => setPagina(h.pagina - 1)}>‹ Anterior</button>
                <span>Página {h.pagina} de {h.paginas}</span>
                <button className="btn-outline" disabled={h.pagina >= h.paginas} onClick={() => setPagina(h.pagina + 1)}>Siguiente ›</button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">Últimas ejecuciones</h3>
        {ej.length === 0 ? <div className="empty-state">Todavía no se ejecutó ningún agente.</div> : (
          <ul className="ag-ejecuciones">
            {ej.map((x) => (
              <li key={x.id}>
                <div className="ag-ejecucion-cabeza">
                  <span className={`tag ${x.ok ? 'tag--green' : 'tag--red'}`}>{x.ok ? 'Bien' : 'Con error'}</span>
                  <b>{x.agenteNombre}</b>
                  <span className="ag-gris">{ORIGENES[x.origen] || x.origen} · {fechaLarga(x.inicio)}{x.actor ? ` · ${x.actor}` : ''}</span>
                </div>
                <p className="ag-pre ag-pre--chico">{x.resumen}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

// ── Pagina ─────────────────────────────────────────────────────────────────────────
export default function Agentes() {
  const { logout } = useAuth();
  const { operaciones: permitido, cargando: cargandoPermiso } = usePermisoOperaciones();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pestana = PESTANAS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'resumen';
  const [flash, setFlash] = useState(null);
  const { data, loading, error, refetch } = useQuery(GET_AGENTES, { fetchPolicy: 'network-only', skip: !permitido });
  const [elegirCasilla, { loading: guardandoCasilla }] = useMutation(ELEGIR_CASILLA);
  const datos = data?.getAgentesOffice;
  const ejecutandose = !!datos?.agentes.some((a) => a.ejecutandose);

  // Mientras un agente corre, se actualiza cada 4 segundos; si no, cada 30 (para el estado de la agenda).
  useEffect(() => {
    if (!permitido) return undefined;
    const t = setInterval(() => refetch(), ejecutandose ? 4000 : 30000);
    return () => clearInterval(t);
  }, [ejecutandose, permitido, refetch]);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), flash.tipo === 'error' ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [flash]);

  if (cargandoPermiso) return <div className="page-container" style={{ maxWidth: 900 }}><div className="spinner" /></div>;
  if (!permitido) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Solo los superadmins con permiso Operaciones pueden ver los agentes.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }

  const ir = (k) => { setParams(k === 'resumen' ? {} : { tab: k }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const onFlash = (texto, tipo = 'ok') => setFlash({ texto, tipo });
  const onCasilla = async (casilla) => {
    try { await elegirCasilla({ variables: { casilla } }); onFlash('Casilla de envío actualizada.'); refetch(); } catch (e) { onFlash(errorDe(e), 'error'); }
  };
  const agente = datos?.agentes.find((a) => a.clave === pestana);
  const pendientes = datos ? datos.enviosPendientes : 0;

  return (
    <div className="page-container cia ag" style={{ maxWidth: 1100 }}>
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>Agentes</h1>
          <span className="ag-subtitulo">Revisan solos lo que les pidas y mandan mails</span>
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>

      <div className="cia-tabs ag-pestanas" role="tablist">
        {PESTANAS.map(([k, t]) => (
          <button key={k} role="tab" aria-selected={pestana === k} className={`cia-tab ${pestana === k ? 'cia-tab--activa' : ''}`} onClick={() => ir(k)}>
            {t}{k === 'envios' && pendientes > 0 && <span className="ag-contador">{pendientes}</span>}
          </button>
        ))}
      </div>

      {flash && <div className={`ag-flash ag-flash--${flash.tipo}`} role="status">{flash.texto}</div>}
      {error && <Aviso tipo="error">{errorDe(error)}</Aviso>}
      {loading && !datos && <div className="spinner" />}

      {datos && pestana === 'resumen' && <Resumen datos={datos} ir={ir} onCasilla={onCasilla} guardandoCasilla={guardandoCasilla} />}
      {datos && agente && <ConfigAgente key={agente.clave} agente={agente} ir={ir} onFlash={onFlash} refetch={refetch} />}
      {pestana === 'admins_monitoreo' && (
        <AdminsMonitoreo onFlash={onFlash} diasInactividad={Number(datos?.agentes.find((a) => a.clave === 'admins_inactivos')?.parametros.find((p) => p.clave === 'diasInactividad')?.valor) || null} />
      )}
      {pestana === 'lista_admins' && <Interesados lista="admins" onFlash={onFlash} />}
      {pestana === 'lista_informe' && <Interesados lista="informe" onFlash={onFlash} />}
      {pestana === 'lista_api' && <Interesados lista="api" onFlash={onFlash} />}
      {pestana === 'envios' && <Envios onFlash={onFlash} />}
    </div>
  );
}
